import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import api from "../../utils/api";
import "./SessionRequestsCard.css";

// Tutor/centre-facing card for managing incoming intro/lesson session
// requests: accept, decline, and (once accepted) mark completed. Marking a
// session completed is the ONLY thing that feeds the public profile's
// "Completed TutorsLinks Lessons" count — see LessonSession on the backend.
const SessionRequestsCard = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/lesson-sessions/mine");
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error("Failed to load session requests:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const respond = async (id, action) => {
    setBusyId(id);
    try {
      await api.patch(`/lesson-sessions/${id}/respond`, { action });
      toast.success(action === "accept" ? "Request accepted" : "Request declined");
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to respond");
    } finally {
      setBusyId(null);
    }
  };

  const complete = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/lesson-sessions/${id}/complete`);
      toast.success("Session marked completed");
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  const studentName = (s) =>
    `${s.student?.firstName || ""} ${s.student?.lastName || ""}`.trim() ||
    "A student";

  const active = sessions.filter((s) =>
    ["requested", "accepted"].includes(s.status),
  );

  if (loading) return null;
  if (active.length === 0) return null;

  return (
    <div className="session-requests-card">
      <h3>Session Requests</h3>
      <div className="session-requests-list">
        {active.map((s) => (
          <div key={s._id} className="session-request-item">
            <div className="session-request-main">
              <p className="session-request-name">
                {studentName(s)} —{" "}
                {s.type === "intro" ? "Intro Session" : "Lesson"}
              </p>
              {s.message && (
                <p className="session-request-message">"{s.message}"</p>
              )}
              <span className={`session-status-pill session-status-${s.status}`}>
                {s.status}
              </span>
            </div>

            <div className="session-request-actions">
              {s.status === "requested" && (
                <>
                  <button
                    className="session-btn session-btn-accept"
                    disabled={busyId === s._id}
                    onClick={() => respond(s._id, "accept")}
                  >
                    Accept
                  </button>
                  <button
                    className="session-btn session-btn-decline"
                    disabled={busyId === s._id}
                    onClick={() => respond(s._id, "decline")}
                  >
                    Decline
                  </button>
                </>
              )}
              {s.status === "accepted" && (
                <button
                  className="session-btn session-btn-complete"
                  disabled={busyId === s._id}
                  onClick={() => complete(s._id)}
                >
                  Mark Completed
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SessionRequestsCard;
